// Global confirm-dialog system — replaces every page's local "Delete?" dialog
// and its confirmDeleteId / open state.
//
// Usage (anywhere under <ConfirmProvider>):
//   const confirm = useConfirm();
//   if (await confirm({ title: "Delete domain?", message: "This can't be undone.", danger: true }))
//     deleteDomain.mutate(id);
//
// Options: { title, message, confirmLabel, cancelLabel, danger }. Resolves to
// true when confirmed, false when cancelled / dismissed.
import { createContext, useContext, useCallback, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { useThemeColors } from "../context/ThemeContext";
import { HEADING_FONT } from "../theme/fonts";

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  message: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  danger: false,
};

export function ConfirmProvider({ children }) {
  const colors = useThemeColors();
  const [state, setState] = useState({ open: false, options: DEFAULTS });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ open: true, options: { ...DEFAULTS, ...options } });
    });
  }, []);

  const close = useCallback((result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  }, []);

  const { options } = state;
  const accent = options.danger ? "#ef4444" : "#818cf8";
  const Icon = options.danger ? WarningAmberRoundedIcon : HelpOutlineRoundedIcon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => close(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            backdropFilter: "blur(12px)",
            borderRadius: 3,
            boxShadow: colors.shadowLg,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                background: `${accent}1f`,
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 17, fontWeight: 700, color: colors.text }}>
              {options.title}
            </Typography>
          </Stack>
        </DialogTitle>
        {options.message && (
          <DialogContent sx={{ pt: 0 }}>
            <Typography
              sx={{ fontSize: 14, color: colors.textMuted, lineHeight: 1.55, pl: 0.25 }}
            >
              {options.message}
            </Typography>
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button
            onClick={() => close(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            {options.cancelLabel}
          </Button>
          <Button
            onClick={() => close(true)}
            autoFocus
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: "#fff",
              background: options.danger
                ? "#ef4444"
                : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              "&:hover": {
                background: options.danger ? "#dc2626" : undefined,
                boxShadow: `0 8px 22px ${accent}55`,
              },
            }}
          >
            {options.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

export default ConfirmProvider;
